import { useApolloClient } from "@apollo/client";
import { ModalOpenButton } from "@msys/ui";
import { Settings as SettingsIcon } from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Badge,
  Button,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { noop } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Agreement, PermissionName } from "../../../../clients/graphqlTypes.js";
import { useFeature } from "../../../../common/FeatureFlags.js";
import { RestrictedByCapabilityAndDocumentPermissionWithDebug } from "../../../auth/RestrictedByCapabilityAndDocumentPermission.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { QUOTE_TEMPLATE_ITEM_TYPES } from "../../../constants.js";
import { BareTreeItem } from "../../../trees/BareTreeItem.js";
import { TreeRightIcons } from "../../../trees/components/TreeRightIcons.js";
import { buildDocPath } from "../../../utils.js";
import { ContentBox } from "../../contents/ContentBox.js";
import { itemHasEstimatedCalculationBox } from "../../doc-items/app-conditions/boxes.js";
import { DecisionManagementBox } from "../../doc-items/boxes/DecisionManagementBox.js";
import { EstimatedCalculationBox } from "../../doc-items/boxes/EstimatedCalculationBox.js";
import { HeaderBox } from "../../doc-items/boxes/HeaderBox.js";
import { ItemFilesBox } from "../../doc-items/boxes/ItemFilesBox.js";
import { ProductBox } from "../../doc-items/boxes/ProductBox.js";
import { RecommendationsConfigBox } from "../../doc-items/boxes/RecommendationsConfigBox.js";
import { RequiredOnCompletionBox } from "../../doc-items/boxes/RequiredOnCompletionBox.js";
import { RulesBox } from "../../doc-items/boxes/RulesBox.js";
import {
  SubelementsBox,
  SubitemComponentProps,
} from "../../doc-items/boxes/SubelementsBox.js";
import { TemplateTypePlaceholderBox } from "../../doc-items/boxes/TemplateTypePlaceholderBox.js";
import { noopRefetchWizardQuestions } from "../../doc-items/withWizardRefetch.js";
import { ItemPropsOverrideModal } from "../../doc-items/modals/ItemPropsOverrideModal.js";
import {
  hasPropOverride,
  isNonComputedProp,
} from "../../doc-items/properties.js";
import { QuoteTemplateUpdateToLatestPublishedVersionButton } from "./buttons/QuoteTemplateUpdateToLatestPublishedVersionButton.js";
import { QuoteTemplatePriceAlert } from "./QuoteTemplatePriceAlert.js";
import {
  TemplateQuoteItem_ChildItemComponentFragment,
  TemplateQuoteItem_ChildItemFragment,
  TemplateQuoteItem_DocumentFragment,
  TemplateQuoteItem_ItemFragment,
  useResolveTemplateMutation,
} from "./TemplateQuoteItem.generated.js";
import { TemplateQuoteLinkedTemplateItemHeaderBox } from "./TemplateQuoteLinkedTemplateItemHeaderBox.js";
import { getData123 } from "./trees.js";

interface Props {
  doc: TemplateQuoteItem_DocumentFragment;
  item: TemplateQuoteItem_ItemFragment;
  pathToDocList: string;
  pathToDoc: string;
  pathToDocPage: string;
  onResolveTemplateRefetchQueries: string[];
  onSetContentRefetchQueries: string[];
  onUpdateDataRefetchQueries: string[];
  refetchQueries?: string[];
  isEditable?: boolean;
}

export const TemplateQuoteItem = ({
  doc,
  item,
  pathToDocList,
  pathToDoc,
  pathToDocPage,
  onResolveTemplateRefetchQueries,
  onSetContentRefetchQueries,
  onUpdateDataRefetchQueries,
  isEditable = true,
}: Props) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const viewer = useUserData().currentUser!;
  const { t } = useTranslate(["QuoteItem", "Templates", "Global"]);
  const isTemplateDebugEnabled = useFeature("TemplateDebug");

  const isOwnTemplate =
    doc.owningSystemOrganisationId === viewer.organisation.id;

  const client = useApolloClient();

  const [resolveTemplateMutation, { loading: resolveTemplateMutationLoading }] =
    useResolveTemplateMutation({ client });

  const resolveTemplate = async () => {
    try {
      await resolveTemplateMutation({
        variables: {
          input: {
            projectId: null,
            docId: doc.id,
            itemId: item.id,
            resolveDepth: 1,
          },
        },
        refetchQueries: onResolveTemplateRefetchQueries,
      });
      navigate(pathToDocPage, { replace: true });
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  const hasEstimatedCalculationBox = itemHasEstimatedCalculationBox(item);

  const ChildItemComponent = React.useMemo(
    () =>
      createChildItemComponent(
        pathToDocPage,
        { viewerPermissions: viewer.organisationPermissions },
        "NONE"
      ),
    [pathToDocPage, viewer.organisationPermissions]
  );

  if (item.linkedQuoteTemplate) {
    return (
      <Stack spacing={1} direction="column">
        {isOwnTemplate &&
          item.linkedQuoteTemplate.latestAvailableVersionNumber &&
          item.linkedQuoteTemplateAtVersionNumber !==
            item.linkedQuoteTemplate.latestAvailableVersionNumber && (
            <Alert
              variant="standard"
              severity="warning"
              action={
                isEditable ? (
                  <QuoteTemplateUpdateToLatestPublishedVersionButton
                    docId={doc.id}
                    linkingItemId={item.id}
                    latestAvailableVersionNumber={
                      item.linkedQuoteTemplate.latestAvailableVersionNumber
                    }
                    pathToDoc={pathToDoc}
                  />
                ) : (
                  <Button
                    component={Link}
                    to={`${buildDocPath(pathToDoc, null)}/edit/items/${
                      item.id
                    }`}
                    color="inherit"
                  >
                    {t("Edit", { ns: "Global" })}
                  </Button>
                )
              }
            >
              <AlertTitle>
                {t("The linked template is outdated!", { ns: "Templates" })}
              </AlertTitle>
              <Stack
                spacing={1}
                sx={{
                  alignItems: "stretch",
                }}
              >
                {t("There is a newer version of this template available", {
                  ns: "Templates",
                })}
                {isTemplateDebugEnabled && (
                  <div
                    style={{
                      fontStyle: "italic",
                      textAlign: "right",
                      alignSelf: "flex-end",
                    }}
                  >
                    <Typography>Debug:</Typography>
                    <div>
                      linked version:{item.linkedQuoteTemplateAtVersionNumber}
                    </div>
                    <div>
                      latest version:
                      {item.linkedQuoteTemplate.latestAvailableVersionNumber}
                    </div>
                  </div>
                )}
                <MuiLink
                  component={Link}
                  to={`${pathToDocList}/${item.linkedQuoteTemplate.id}/${item.linkedQuoteTemplate.latestAvailableVersionNumber}`}
                  target="_blank"
                  color="inherit"
                  variant="h4"
                >
                  {t("See the available version", { ns: "Templates" })}
                </MuiLink>
              </Stack>
            </Alert>
          )}
        <TemplateQuoteLinkedTemplateItemHeaderBox
          docId={doc.id}
          item={item}
          isEditable={isEditable}
        />
        {!item.isRootItem && item.type !== "unpaid" && (
          <RulesBox
            projectId={null}
            docId={doc.id}
            item={item}
            canEdit={isEditable}
            docType="TEMPLATE"
            documentViewerPermissions={viewer.organisationPermissions}
            refetchWizardQuestions={noopRefetchWizardQuestions}
          />
        )}
        {hasEstimatedCalculationBox && (
          <EstimatedCalculationBox
            projectId={null}
            docId={doc.id}
            item={item}
            contractType={doc.contractType}
            isReadOnly={!isEditable}
            viewerIsContractor={isEditable}
            showDetailedCalculation={false}
            onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
          />
        )}
        <Stack direction="column" spacing={1}>
          <Button
            component={Link}
            color="primary"
            to={buildDocPath(
              `${pathToDocList}/${item.linkedQuoteTemplate.id}`,
              item.linkedQuoteTemplateAtVersionNumber ?? null
            )}
            variant="contained"
          >
            {t("View Template Details", { ns: "QuoteItem" })}
          </Button>
          {/* hide resolveTemplate button, creating items by template is out-of-scope for MEIS-6177 */}
          {item.linkedQuoteTemplateAtVersionNumber && (
            <Button
              onClick={resolveTemplate}
              loading={resolveTemplateMutationLoading}
              color="secondary"
              variant="outlined"
              disabled={!isEditable}
            >
              {t("Resolve template", { ns: "QuoteItem" })}
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }

  const hasConfigurablePropsOverrides = item.props2.some(
    property => isNonComputedProp(property) && property.customizable
  );
  const hasPropsOverrides = item.itemPropsOverrides.some(hasPropOverride);

  return (
    <Stack direction="column" spacing={1}>
      {item.isRootItem && !isEditable && <QuoteTemplatePriceAlert />}
      <HeaderBox
        projectId={null}
        docId={doc.id}
        docType="TEMPLATE"
        item={item}
        viewMode={isEditable ? "edit" : null}
        setViewMode={noop}
        onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
        PropertiesSectionActionButtons={
          doc.customizable &&
          item.originVersionNumber &&
          hasConfigurablePropsOverrides ? (
            <RestrictedByOrganisationPermissionWithDebug permission="template_document_update">
              <ModalOpenButton
                Modal={ItemPropsOverrideModal}
                modalProps={{
                  templateId: doc.id,
                  itemId: item.id,
                  versionNumber: item.originVersionNumber,
                }}
              >
                <IconButton size="small" color="primary">
                  <Badge
                    color="secondary"
                    variant="dot"
                    invisible={!hasPropsOverrides}
                  >
                    <Tooltip
                      title={t("Configure properties", { ns: "QuoteItem" })}
                    >
                      <SettingsIcon />
                    </Tooltip>
                  </Badge>
                </IconButton>
              </ModalOpenButton>
            </RestrictedByOrganisationPermissionWithDebug>
          ) : null
        }
        documentViewerPermissions={viewer.organisationPermissions}
      />
      {!item.isRootItem && item.type !== "unpaid" && (
        <RulesBox
          projectId={null}
          docId={doc.id}
          item={item}
          canEdit={isEditable}
          docType="TEMPLATE"
          documentViewerPermissions={viewer.organisationPermissions}
          refetchWizardQuestions={noopRefetchWizardQuestions}
        />
      )}
      {item.type === "paid" && (
        <ProductBox
          project={null}
          docId={doc.id}
          docType="TEMPLATE"
          documentViewerPermissions={viewer.organisationPermissions}
          item={item}
          isEditable={isEditable}
          viewerIsContractor={isOwnTemplate}
          onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
          refetchWizardQuestions={noopRefetchWizardQuestions}
        />
      )}
      {hasEstimatedCalculationBox && (
        <EstimatedCalculationBox
          key={`${item.product?.articleNumber}-${item.product?.supplierId}-${item.product?.title}`}
          projectId={null}
          docId={doc.id}
          item={item}
          contractType={doc.contractType}
          isReadOnly={!isEditable}
          viewerIsContractor={isEditable}
          onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
        />
      )}
      {item.type === "decision" && isOwnTemplate && (
        <DecisionManagementBox
          item={item}
          doc={{
            id: doc.id,
            docType: "TEMPLATE",
            templateIsDraft: !doc.hasAnyPublishedVersion,
            organisationId: doc.owningSystemOrganisationId,
          }}
          projectId={null}
          onItemDeleted={() => {
            navigate(pathToDocPage, { replace: true });
          }}
          isEditable={isEditable}
          refetchWizardQuestions={noopRefetchWizardQuestions}
        />
      )}
      <RequiredOnCompletionBox
        project={null}
        docId={doc.id}
        document={{ viewerPermissions: viewer.organisationPermissions }}
        item={item}
        canEdit={isEditable}
        onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
      />
      <ItemFilesBox
        projectId={null}
        docId={doc.id}
        docType="TEMPLATE"
        viewerPermissions={viewer.organisationPermissions}
        item={item}
        isEditable={isEditable}
        onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
      />
      <SubelementsBox<TemplateQuoteItem_ChildItemFragment>
        projectId={null}
        doc={{
          id: doc.id,
          docType: "TEMPLATE",
          templateIsDraft: !doc.hasAnyPublishedVersion,
        }}
        item={item}
        subitemComponent={ChildItemComponent}
        pathToDocPage={pathToDocPage}
        documentItemTypes={QUOTE_TEMPLATE_ITEM_TYPES}
        canChangeVisibility={true}
        isEditable={isEditable}
      />
      <RestrictedByCapabilityAndDocumentPermissionWithDebug
        capability="advancedTemplating"
        permission="template_document_update_advancedTemplating"
        document={{ viewerPermissions: viewer.organisationPermissions }}
        otherwise={
          <RecommendationsConfigBox
            item={item}
            canEdit={false}
            documentItemTypes={QUOTE_TEMPLATE_ITEM_TYPES}
            docType="TEMPLATE"
            documentViewerPermissions={viewer.organisationPermissions}
            refetchWizardQuestions={noopRefetchWizardQuestions}
          />
        }
      >
        <RecommendationsConfigBox
          item={item}
          canEdit={isEditable}
          documentItemTypes={QUOTE_TEMPLATE_ITEM_TYPES}
          docType="TEMPLATE"
          documentViewerPermissions={viewer.organisationPermissions}
          refetchWizardQuestions={noopRefetchWizardQuestions}
        />
      </RestrictedByCapabilityAndDocumentPermissionWithDebug>
      <RestrictedByCapabilityAndDocumentPermissionWithDebug
        capability="advancedTemplating"
        permission="template_document_update_advancedTemplating"
        document={{ viewerPermissions: viewer.organisationPermissions }}
        otherwise={
          <TemplateTypePlaceholderBox
            projectId={null}
            doc={doc}
            item={item}
            isEditable={false}
            onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
            docType="TEMPLATE"
            documentViewerPermissions={viewer.organisationPermissions}
            refetchWizardQuestions={noopRefetchWizardQuestions}
          />
        }
      >
        <TemplateTypePlaceholderBox
          projectId={null}
          doc={doc}
          item={item}
          isEditable={isEditable}
          onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
          docType="TEMPLATE"
          documentViewerPermissions={viewer.organisationPermissions}
          refetchWizardQuestions={noopRefetchWizardQuestions}
        />
      </RestrictedByCapabilityAndDocumentPermissionWithDebug>
      {item.contents.length > 0 && (
        <ContentBox
          itemId={item.id}
          content={item.contents}
          refetchQueries={onSetContentRefetchQueries}
          isEditable={isEditable}
        />
      )}
    </Stack>
  );
};

const createChildItemComponent =
  <T extends TemplateQuoteItem_ChildItemComponentFragment>(
    pathToDocPage: string,
    document: {
      viewerPermissions: PermissionName[];
    },
    docAgreement: Agreement
  ) =>
  (itemProps: SubitemComponentProps<T>) => {
    const [Data123Left, Data123Right] = React.useMemo(
      () => getData123(itemProps.item),
      [itemProps.item]
    );

    const RightIcons = React.useMemo(
      () => (
        <TreeRightIcons
          document={document}
          item={itemProps.item}
          isVisibleToOtherSide={itemProps.item.isVisibleToOtherSide}
        />
      ),
      [itemProps.item]
    );

    return (
      <BareTreeItem
        docAgreement={docAgreement}
        Data123Left={Data123Left}
        Data123Right={Data123Right}
        RightIcons={RightIcons}
        showPath={!itemProps.item.isDecisionOption}
        item={itemProps.item}
        isRootItem={itemProps.isRootItem}
        depth={itemProps.depth}
        isGreyedOut={itemProps.isGreyedOut}
        isHidden={itemProps.isHidden}
        isPriceHidden={itemProps.isPriceHidden}
        onClick={itemProps.onClick}
        subcontractPath={null}
        to={
          itemProps.to !== undefined
            ? itemProps.to
            : `${pathToDocPage}/items/${itemProps.item.id}`
        }
      />
    );
  };
